import type { MfgOverheadBucketTreeNode } from "@/lib/manufacturing/overheadTypes";
import { adminDangerGhostBtnClass, ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { useAuth } from "@/contexts/AuthContext";
import type { ManufacturingOverheadBucketsState } from "./useManufacturingOverheadBuckets";
import { OverheadBucketEditForm } from "./OverheadBucketEditForm";

type Nesting = {
  draggingId: string | null;
  hoverId: string | null;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onHover: (id: string | null) => void;
  onDrop: (sourceId: string, target: MfgOverheadBucketTreeNode | null) => void;
};

type Props = {
  nodes: MfgOverheadBucketTreeNode[];
  depth?: number;
  state: ManufacturingOverheadBucketsState;
  plantId: string;
  upcomingConsumptionOn: string | null;
  consumptionConfigured: boolean;
  nesting: Nesting;
  collapsedIds: Record<string, boolean>;
  onToggleCollapsed: (id: string) => void;
};

export function OverheadBucketTree({
  nodes,
  depth = 0,
  state,
  plantId,
  upcomingConsumptionOn,
  consumptionConfigured,
  nesting,
  collapsedIds,
  onToggleCollapsed,
}: Props) {
  const canDelete = useAuth().hasRole("owner");
  if (nodes.length === 0 && depth === 0) {
    return <div className="text-sm text-gray-500 dark:text-zinc-400">No buckets yet.</div>;
  }

  return (
    <div className="space-y-2">
      {depth === 0 && nesting.draggingId ? (
        <div
          className={
            "rounded-lg border border-dashed px-3 py-2 text-xs " +
            (nesting.hoverId === "top"
              ? "border-indigo-400 bg-indigo-50 text-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-100"
              : "border-gray-300 text-gray-500 dark:border-zinc-600 dark:text-zinc-400")
          }
          onDragOver={(event) => {
            event.preventDefault();
            nesting.onHover("top");
          }}
          onDrop={(event) => {
            event.preventDefault();
            nesting.onHover(null);
            nesting.onDrop(event.dataTransfer.getData("text/plain"), null);
          }}
        >
          Drop here to leave it at the top
        </div>
      ) : null}
      <ul className={depth === 0 ? "space-y-1" : "mt-1 space-y-1 border-l border-gray-100 pl-3 dark:border-zinc-700"}>
        {nodes.map((node) => {
          const canReceive = depth === 0 && nesting.draggingId !== null && nesting.draggingId !== node.id;
          const collapsed = node.children.length > 0 && collapsedIds[node.id] === true;
          return (
            <li key={node.id}>
              {state.editingId === node.id ? (
                <OverheadBucketEditForm
                  title="Edit bucket"
                  draft={state.editDraft}
                  setDraft={state.setEditDraft}
                  bucketTypes={state.bucketTypes}
                  parentOptions={state.flatBuckets.filter((b) => b.id !== node.id && !b.parent_bucket_id)}
                  busy={state.actionBusy}
                  onCancel={state.cancelEdit}
                  onSubmit={() => void state.submitEdit()}
                />
              ) : (
                <div
                  data-bucket-row=""
                  className={
                    "flex flex-wrap items-start justify-between gap-2 rounded-lg border bg-gray-50/80 px-3 py-2 dark:bg-zinc-800/40 " +
                    (canReceive && nesting.hoverId === node.id
                      ? "border-indigo-400 ring-2 ring-indigo-300 dark:border-indigo-500"
                      : "border-gray-100 dark:border-zinc-700")
                  }
                  onDragOver={canReceive ? (event) => {
                    event.preventDefault();
                    nesting.onHover(node.id);
                  } : undefined}
                  onDrop={canReceive ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    nesting.onHover(null);
                    nesting.onDrop(event.dataTransfer.getData("text/plain"), node);
                  } : undefined}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        draggable={!state.actionBusy}
                        aria-label={`Drag ${node.name}`}
                        title="Drag onto a top-level bucket to nest it"
                        className="cursor-grab select-none px-1 text-sm text-gray-400 active:cursor-grabbing dark:text-zinc-500"
                        onDragStart={(event) => {
                          event.dataTransfer.setData("text/plain", node.id);
                          event.dataTransfer.effectAllowed = "move";
                          const row = event.currentTarget.closest("[data-bucket-row]");
                          if (row instanceof HTMLElement) event.dataTransfer.setDragImage(row, 24, 16);
                          window.setTimeout(() => nesting.onDragStart(node.id), 0);
                        }}
                        onDragEnd={() => nesting.onDragEnd()}
                      >
                        ⋮⋮
                      </span>
                      {node.children.length > 0 ? (
                        <button
                          type="button"
                          className="px-0.5 text-xs text-gray-500 dark:text-zinc-400"
                          aria-expanded={!collapsed}
                          aria-label={collapsed ? `Show items in ${node.name}` : `Hide items in ${node.name}`}
                          onClick={() => onToggleCollapsed(node.id)}
                        >
                          {collapsed ? `▸ ${node.children.length}` : "▾"}
                        </button>
                      ) : null}
                      <span className="font-medium text-gray-900 dark:text-zinc-100">{node.name}</span>
                  <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200">
                    {node.bucket_type_name}
                  </span>
                  {!node.is_active ? (
                    <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-700 dark:bg-zinc-700 dark:text-zinc-200">
                      inactive
                    </span>
                  ) : null}
                  {node.is_system_default ? (
                    <span className="text-[10px] text-gray-500 dark:text-zinc-400">default</span>
                  ) : null}
                  {node.is_aggregate ? (
                    <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-700 dark:bg-zinc-700 dark:text-zinc-200">
                      folder
                    </span>
                  ) : null}
                  {node.retired_on ? (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                      retired
                    </span>
                  ) : null}
                </div>
                <div className="mt-0.5 text-xs text-gray-600 dark:text-zinc-400">
                  <span className="font-mono">{node.bucket_key}</span>
                  {node.category ? <span className="mx-1">·</span> : null}
                  {node.category ? <span>{node.category}</span> : null}
                </div>
                {node.notes ? (
                  <div className="mt-1 text-xs text-gray-500 dark:text-zinc-400">{node.notes}</div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  className={ghostBtnClass}
                  disabled={state.actionBusy}
                  onClick={() => state.startEdit(node)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className={ghostBtnClass}
                  disabled={state.actionBusy}
                  onClick={() => {
                    if (node.retired_on) {
                      if (window.confirm(`Bring ${node.name} back into upcoming overhead records?`)) {
                        void state.retireBucket(node, plantId, true);
                      }
                      return;
                    }
                    if (!consumptionConfigured || !upcomingConsumptionOn) {
                      window.alert("Set a consumption day on Period values before retiring a bucket.");
                      return;
                    }
                    const confirmed = window.confirm(
                      `Retire ${node.name}?\n\nThis leaves it out of the overhead recorded on ${upcomingConsumptionOn}. If this month's payment already went out, wait until after that date.`,
                    );
                    if (confirmed) void state.retireBucket(node, plantId, false);
                  }}
                >
                  {node.retired_on ? "Restore" : "Retire"}
                </button>
                <button
                  type="button"
                  className={ghostBtnClass}
                  disabled={state.actionBusy}
                  onClick={() => void state.toggleActive(node)}
                >
                  {node.is_active ? "Disable" : "Enable"}
                </button>
                {canDelete ? (
                  <button
                    type="button"
                    className={adminDangerGhostBtnClass}
                    disabled={state.actionBusy}
                    onClick={() => {
                      if (node.children.length > 0) {
                        window.alert(`Move the buckets under ${node.name} out before deleting it.`);
                        return;
                      }
                      const confirmed = window.confirm(
                        `Delete ${node.name}?\n\nThis permanently removes the bucket and the payments recorded on it. This cannot be undone.`,
                      );
                      if (confirmed) void state.deleteBucket(node.id);
                    }}
                  >
                    Delete
                  </button>
                ) : null}
              </div>
            </div>
          )}
          {node.children.length > 0 && !collapsed ? (
            <OverheadBucketTree
              nodes={node.children}
              depth={depth + 1}
              state={state}
              plantId={plantId}
              upcomingConsumptionOn={upcomingConsumptionOn}
              consumptionConfigured={consumptionConfigured}
              nesting={nesting}
              collapsedIds={collapsedIds}
              onToggleCollapsed={onToggleCollapsed}
            />
          ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
